import React from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography, Icons, DateTimeStandard } from "@curves/ui-base";
import { ProductInfo } from "@curves/ui-shared";
import { parseDate } from "@util/moment";
import { formatCurrencyFn } from "@util/utils";

/**
 * 商品庫存摘要卡片
 * @param {Object} props
 * @param {Object} props.product - 商品資訊（sku、name、spec、category、imageUrl）
 * @param {string} [props.lastInventoryDate] - 上次盤點日
 * @param {number} [props.defaultPrice] - 預設販售價格
 * @param {number} [props.sellableQty] - 可販售數量
 * @param {boolean} [props.canAdjust] - 是否可調整可販售數量
 * @param {Function} [props.onEditSellableQty] - 點擊可販售數量時的回調
 * @param {string} [props.className] - 自定義樣式類名
 */
const StockSummaryCard = ({ product = {}, lastInventoryDate, defaultPrice, sellableQty, canAdjust = false, onEditSellableQty, className = "" }) => {
	// 功能：格式化顯示值，無資料時統一顯示破折號
	const displayLastInventoryDate = lastInventoryDate ? parseDate(lastInventoryDate, DateTimeStandard.DisplayDateBySlash) : "—";
	const displayDefaultPrice = defaultPrice != null ? formatCurrencyFn(defaultPrice, { symbol: "$" }) : "—";
	const displaySellableQty = sellableQty != null ? sellableQty : "—";

	return (
		<Box className={`stock-summary-card ${className}`}>
			<Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={3}>
				{/* 左側：商品資訊 */}
				<Box className="stock-summary-product">
					<ProductInfo product={product} className="stock-summary-product-info" imageSize={120} />
				</Box>

				{/* 右側：摘要資訊 */}
				<Box className="stock-summary-meta">
					<Box className="stock-summary-meta-item">
						<Typography variant="body2" className="stock-summary-meta-label">
							上次盤點日
						</Typography>
						<Typography variant="body1" className="stock-summary-meta-value">
							{displayLastInventoryDate}
						</Typography>
					</Box>
					<Box className="stock-summary-meta-item">
						<Typography variant="body2" className="stock-summary-meta-label">
							預設販售價格
						</Typography>
						<Typography variant="body1" className="stock-summary-meta-value">
							{displayDefaultPrice}
						</Typography>
					</Box>
					<Box className="stock-summary-meta-item">
						<Typography variant="body2" className="stock-summary-meta-label">
							可販售數量
						</Typography>
						<Box className="stock-summary-meta-value">
							{/* 功能：canAdjust 時點擊觸發編輯，圖示依權限顯示/隱藏 */}
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								spacing={1}
								onClick={canAdjust ? onEditSellableQty : undefined}
								style={{ cursor: canAdjust ? "pointer" : "default" }}
							>
								<Typography variant="body1" className="mb-0">
									{displaySellableQty}
								</Typography>
								<span style={{ visibility: canAdjust ? "visible" : "hidden", display: "inline-flex" }}>
									<Icons.Exclamation htmlColor="#A3A3A3" fontSize="small" />
								</span>
							</Stack>
						</Box>
					</Box>
				</Box>
			</Stack>
		</Box>
	);
};

StockSummaryCard.propTypes = {
	product: PropTypes.shape({
		sku: PropTypes.string,
		name: PropTypes.string,
		spec: PropTypes.string,
		category: PropTypes.string,
		imageUrl: PropTypes.string,
	}),
	lastInventoryDate: PropTypes.string,
	defaultPrice: PropTypes.number,
	sellableQty: PropTypes.number,
	canAdjust: PropTypes.bool,
	onEditSellableQty: PropTypes.func,
	className: PropTypes.string,
};

export default StockSummaryCard;
